import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft,
  FloppyDisk,
  Tag,
  X,
  Calendar
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import FileAttachments from '../../components/FileAttachments';

const categories = ['Reagent', 'Enzyme', 'Buffer', 'Media', 'Antibiotic', 'Chemical', 'Consumable', 'Equipment', 'Other'];
const units = ['mL', 'L', 'g', 'kg', 'mg', 'ug', 'units', 'pieces', 'boxes', 'bottles', 'tubes', 'plates'];

const InventoryForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Reagent',
    quantity: '',
    unit: 'mL',
    location: '',
    catalog_number: '',
    supplier: '',
    lot_number: '',
    expiration_date: '',
    minimum_quantity: '',
    tags: []
  });

  useEffect(() => {
    if (isEdit && id) {
      loadItem();
    }
  }, [id, isEdit]);

  const loadItem = async () => {
    try {
      const data = await api.getInventoryItem(id);
      setFormData({
        ...data,
        quantity: data.quantity?.toString() || '',
        minimum_quantity: data.minimum_quantity?.toString() || '',
        expiration_date: data.expiration_date?.split('T')[0] || ''
      });
    } catch (error) {
      toast.error('Failed to load item');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const submitData = {
      ...formData,
      quantity: parseFloat(formData.quantity) || 0,
      minimum_quantity: parseFloat(formData.minimum_quantity) || 0,
      expiration_date: formData.expiration_date || null
    };

    try {
      if (isEdit) {
        await api.updateInventoryItem(id, submitData);
        toast.success('Item updated');
        navigate(`/inventory/${id}`);
      } else {
        const result = await api.createInventoryItem(submitData);
        toast.success('Item added');
        navigate(`/inventory/${result.id}`);
      }
    } catch (error) {
      toast.error('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="inventory-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/inventory" className="p-2 hover:bg-zinc-100 rounded-sm">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="heading-display">{isEdit ? 'Edit Item' : 'Add Inventory Item'}</h1>
          <p className="text-zinc-500 mt-1">{isEdit ? 'Update item details' : 'Add a new item to inventory'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card-flat p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Item name"
                    className="input-field w-full mt-1"
                    required
                    data-testid="inventory-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="input-field w-full mt-1"
                    data-testid="inventory-category-select"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    name="quantity"
                    type="number"
                    step="0.01"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="0"
                    className="input-field w-full mt-1"
                    required
                    data-testid="inventory-quantity-input"
                  />
                </div>
                <div>
                  <Label htmlFor="unit">Unit *</Label>
                  <select
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    className="input-field w-full mt-1"
                    data-testid="inventory-unit-select"
                  >
                    {units.map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="minimum_quantity">Minimum Quantity</Label>
                  <Input
                    id="minimum_quantity"
                    name="minimum_quantity"
                    type="number"
                    step="0.01"
                    value={formData.minimum_quantity}
                    onChange={handleChange}
                    placeholder="Alert threshold"
                    className="input-field w-full mt-1"
                    data-testid="inventory-min-quantity-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Freezer A, Shelf 3"
                  className="input-field w-full mt-1"
                  data-testid="inventory-location-input"
                />
              </div>
            </div>

            {/* Supplier Info */}
            <div className="card-flat p-6 space-y-4">
              <h3 className="heading-card">Supplier Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="supplier">Supplier</Label>
                  <Input
                    id="supplier"
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleChange}
                    placeholder="e.g., Thermo Fisher"
                    className="input-field w-full mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="catalog_number">Catalog Number</Label>
                  <Input
                    id="catalog_number"
                    name="catalog_number"
                    value={formData.catalog_number}
                    onChange={handleChange}
                    placeholder="e.g., A12345"
                    className="input-field w-full mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lot_number">Lot Number</Label>
                  <Input
                    id="lot_number"
                    name="lot_number"
                    value={formData.lot_number}
                    onChange={handleChange}
                    placeholder="Batch/Lot number"
                    className="input-field w-full mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="expiration_date">Expiration Date</Label>
                  <Input
                    id="expiration_date"
                    name="expiration_date"
                    type="date"
                    value={formData.expiration_date}
                    onChange={handleChange}
                    className="input-field w-full mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tags */}
            <div className="card-flat p-6">
              <Label>Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag"
                  className="input-field flex-1"
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  <Tag size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 px-2 py-1 text-sm rounded-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-400">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <Button type="submit" className="btn-primary w-full" disabled={saving} data-testid="save-inventory-btn">
              {saving ? (
                <>
                  <div className="spinner w-4 h-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FloppyDisk size={16} className="mr-2" />
                  {isEdit ? 'Update Item' : 'Add Item'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>

      {/* File Attachments (only show on edit) */}
      {isEdit && id && (
        <div className="card-flat p-5 mt-4">
          <FileAttachments entityType="inventory" entityId={id} />
        </div>
      )}
    </div>
  );
};

export default InventoryForm;
